import React, { FC, useEffect, useState } from "react";
import { Button } from "@mui/material";
import { useForm } from "react-hook-form";
import { darkModeCardStyle } from "./PaymentCardNewForm.style";
import { Loading } from "../../../components";
import { ISquareTokenizeResponse } from "../../../services/card-create/card-create.interface";
import { useCardStatus } from "../../../services/card-status/card-status.service";
import { SwitchController } from "../../../components/Controllers";
import { CardCreateFormType } from "../../../services/card-create/card-create.schema";

interface PaymentCardNewFormProps {
  onSubmit: (tokenize: ISquareTokenizeResponse, data: CardCreateFormType) => void;
  buttonText: string;
  hasError?: boolean;
  onErrorClear?: () => void;
}

const PaymentCardNewForm: FC<PaymentCardNewFormProps> = ({ onSubmit, buttonText, hasError, onErrorClear }) => {
  const { paymentMethod, loading } = useCardStatus();
  const SQUARE_SCRIPT_URL = process.env.REACT_APP_SQUARE_SCRIPT_URL as string;
  const SQUARE_APPLICATION_ID = process.env.REACT_APP_SQUARE_APPLICATION_ID as string;
  const [card, setCard] = useState<any>(null);
  const [Square, setSquare] = useState<any>(null);
  const [tokenizeError, setTokenizeError] = useState<string | null>(null);
  const { handleSubmit, control, setValue } = useForm<CardCreateFormType>({
    mode: "onSubmit",
    defaultValues: {
      setAsdefault: false,
    },
  });

  useEffect(() => {
    if (paymentMethod) {
      setValue("setAsdefault", !paymentMethod.haveCard ? true : false);
      const script = document.createElement("script");

      script.type = "text/javascript";
      script.src = SQUARE_SCRIPT_URL;
      script.async = true;
      script.onload = () => {
        setSquare((window as any).Square);
      };

      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
      };
    }
  }, [paymentMethod]);

  useEffect(() => {
    const payments = Square?.payments(SQUARE_APPLICATION_ID, paymentMethod?.locationSquareId);

    const createCard = async (payments: any) => {
      const card = await payments?.card({ style: darkModeCardStyle });

      await card?.attach("#card-container");
      setCard(card);
    };

    createCard(payments);
  }, [Square?.payments]);

  useEffect(() => {
    if (!card) return;
    const handleCardInput = () => {
      setTokenizeError(null);
      onErrorClear?.();
    };
    card.addEventListener("focusClassAdded", handleCardInput);
    return () => {
      card.removeEventListener("focusClassAdded", handleCardInput);
    };
  }, [card, onErrorClear]);

  const handleSubmitForm = async (data: CardCreateFormType) => {
    setTokenizeError(null);
    try {
      const result = await card?.tokenize();

      if (result.status === "OK") {
        onSubmit(result, data);
      } else {
        const errorMessage = result.errors?.[0]?.message || "Invalid card details. Please check and try again.";
        setTokenizeError(errorMessage);
      }
    } catch (e) {
      console.error(e);
      setTokenizeError("Something went wrong. Please try again.");
    };
  };

  if (loading) {
    return <Loading centerAll />;
  }

  return <>
    <form onSubmit={handleSubmit(handleSubmitForm)} className="h-full flex flex-col justify-between">
      {!card ? <Loading /> : null}

      <div className="flex-1 overflow-y-auto pb-4">
        <div id="card-container"/>

        {tokenizeError && (
          <p className="text-[#ED7857] text-sm mt-2">{tokenizeError}</p>
        )}

        <SwitchController
          label="Set as default payment method"
          control={control}
          name="setAsdefault"
          disabled={!paymentMethod?.haveCard ? true : false}
        />
      </div>

      <Button
        fullWidth
        id="paymentCardNewFormButton"
        color="primary"
        type="submit"
        className="!capitalize"
        disabled={!!tokenizeError || !!hasError}
      >
        {buttonText}
      </Button>

    </form>
  </>;
};

export default PaymentCardNewForm;
